import { baseUrl } from '../../utilities';

import {
  type AvailableFactor,
  type ErrorMessage,
  type ProtectedUrlMethods,
} from './models';

interface ReadAvailableFactorsResponse {
  name:
    | 'MFA_FACTOR_TOTP'
    | 'MFA_FACTOR_SMS'
    | 'MFA_FACTOR_EMAIL'
    | 'MFA_FACTOR_KBA'
    | 'MFA_FACTOR_IDAAS'
    | 'MFA_FACTOR_TEST';
  registered: boolean;
  required: boolean;
}

/** Retrieves available MFA factors, ignoring unregistered or unknown factors */
export async function readAvailableFactors(): Promise<AvailableFactor[]> {
  const response = await fetch(`${baseUrl}/ECMv6/api/authms/api/mfa/factors`);
  const data = (await response.json()) as ReadAvailableFactorsResponse[];
  const result: AvailableFactor[] = [];
  const anyIdaasFactor = data.find(({ name }) => name === 'MFA_FACTOR_IDAAS');

  if (anyIdaasFactor) {
    return [{ type: 'idaas' }];
  }

  data.forEach((factor) => {
    switch (factor.name) {
      case 'MFA_FACTOR_TOTP':
        result.push({ type: 'totp', isRegistered: factor.registered });
        break;
      case 'MFA_FACTOR_TEST':
        if (['development', 'test'].includes(process.env.NODE_ENV ?? '')) {
          result.push({ type: 'test' });
        }

        break;

      // Not fully supported on backend yet
      // case 'MFA_FACTOR_SMS':
      //   result.push('sms');
      //   break;
      // case 'MFA_FACTOR_EMAIL':
      //   result.push('email');
      //   break;

      default:
        break;
    }
  });

  return result;
}

/** Submits a TOTP code for validation */
export async function validateTotpCode(
  code: string
): Promise<boolean | ErrorMessage> {
  const response = await fetch(
    `${baseUrl}/ECMv6/api/authms/api/totp/validate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
      }),
    }
  );

  const { status } = (await response.json()) as { status: 'VALID' | 'INVALID' };

  return status === 'VALID';
}

/** Submits an OTP code for validation */
export async function validateOtpCode(
  code: string,
  type: 'sms' | 'email'
): Promise<boolean | ErrorMessage> {
  const response = await fetch(`${baseUrl}/ECM/mfa/validate-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      otp: code,
      mfaOption: otpMfaOptions[type],
    }),
  });

  if (response.status === 200) {
    return true;
  }

  if (response.status === 401) {
    return false;
  }

  return `Unexpected response status when validating OTP code: ${response.status}`;
}

const otpMfaOptions: Record<'sms' | 'email', string> = {
  sms: 'MFA_FACTOR_SMS',
  email: 'MFA_FACTOR_EMAIL',
};

/**
 * Retrieves the list of requests that are configured for MFA protection.
 */
export async function getProtectedUrls(): Promise<
  Record<ProtectedUrlMethods, string[]>
> {
  const response = await fetch(
    `${baseUrl}/ECMv6/api/authms/api/mfa/protectedUri`
  );

  const protectedRequests = (
    (await response.json()) as {
      data: [{ url: string; method: ProtectedUrlMethods }];
    }
  ).data;

  const prefixedProtectedRequests = protectedRequests.map(
    ({ url, method }) => ({
      url: url.startsWith('/ECM/') ? url : `/ECMv6/api${url}`,
      method,
    })
  );

  const protectedUrlsByHttpMethod = prefixedProtectedRequests.reduce<
    Record<ProtectedUrlMethods, string[]>
  >(
    (acc, { method, url }) => {
      acc[method].push(url);

      return acc;
    },
    { ALL: [], POST: [], GET: [], PUT: [], DELETE: [] }
  );

  return protectedUrlsByHttpMethod;
}

/**
 * Requests that the backend send a code to the user's contact device.
 */
export function sendOtpCode(sentFactorType: 'sms' | 'email'): Promise<void> {
  throw new Error(`Not implemented: ${sentFactorType}`);
}

interface MfaStatusResponse {
  verified: boolean;
}

/**
 * Queries the current validity of the user session's MFA verification.
 */
export async function checkAlreadyVerified(): Promise<boolean> {
  const response = await fetch(`${baseUrl}/ECMv6/api/authms/api/mfa`);
  const { verified } = (await response.json()) as MfaStatusResponse;

  return verified;
}
